/**
 * What a sheet floating over the app owes a keyboard.
 *
 * Four things, and each of them is a way a dialog is unusable without a mouse:
 * focus moves into the sheet when it opens, Tab does not wander out of it into
 * the mail behind, Escape closes it, and focus goes back to the control that
 * opened it rather than to the top of the document -- which is where the
 * browser puts it when the element that had focus is unmounted, and it is why
 * closing a dialog used to cost a screen-reader user their place entirely.
 *
 * It is a hook rather than a wrapper component because the two sheets that use
 * it do not share a shape: one is a list with its own search box, the other is
 * a table of keys. What they share is this behaviour, and nothing else.
 *
 * # Why the count is exported
 *
 * The app's shortcuts are bound at `window` and act on the message under the
 * cursor from anywhere. That is right while the mail is what is on screen and
 * wrong the moment something is over it: `e` pressed while reading the list of
 * what the keys do -- a document whose entire subject is what the keys do --
 * would archive the message behind the sheet, and nothing on screen would say
 * so. So the sheets say they are up, and `app.tsx` stands down while they are.
 *
 * Not a boolean. Two sheets can be closing and opening in the same tick, and a
 * flag written by both would leave the app's keyboard switched off after the
 * last one had gone.
 */

import { computed, signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";

const open = signal(0);

/** True while any modal sheet is on screen. */
export const modalOpen = computed(() => open.value > 0);

/**
 * Everything inside the sheet that a Tab can land on.
 *
 * Disabled controls are excluded because the browser skips them anyway, and a
 * trap that counted one as the last stop would send Tab to a control that
 * cannot take focus -- which loses the focus ring altogether.
 */
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Wire a sheet up as a dialog. Put the returned ref on the element that carries
 * `role="dialog"`, and give it `tabIndex={-1}` so it can take focus itself.
 *
 * `onClose` is read through a ref so that a caller may pass a fresh closure on
 * every render -- the listener is registered once, on open, and re-registering
 * it per render would move the sheet's focus back to the top each time.
 */
export function useDialog<T extends HTMLElement>(onClose: () => void) {
  const sheet = useRef<T | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement;
    // The sheet itself, not the first control in it: the sheet carries the
    // accessible name, so this is the moment a screen reader says what has just
    // opened. Focusing the first button would announce the button instead.
    sheet.current?.focus();
    open.value += 1;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Stopped here so it means one thing. Escape also closes the message
        // and clears a selection, and a press meant for the sheet must not
        // rearrange the app underneath it on its way out.
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== "Tab") return;

      const root = sheet.current;
      if (!root) return;
      const stops = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (!first || !last) {
        // A sheet with nothing focusable in it still keeps the Tab: letting it
        // through would move focus to whatever is behind the overlay, where a
        // click cannot follow it.
        event.preventDefault();
        root.focus();
        return;
      }

      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      open.value -= 1;
      // Only if it is still in the document. A sheet opened from a button that
      // has since been unmounted -- Settings left behind by the back button --
      // has nothing to give focus back to, and calling `focus()` on a detached
      // node silently sends it to `<body>`.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  return sheet;
}
